// src/pages/Faq.jsx

import React from "react";
import PageHero from "../components/PageHero";
import {
  ChevronDown,
  HelpCircle,
  ShieldCheck,
  Truck,
  Factory,
  Layers3,
} from "lucide-react";

const faqs = [
  {
    icon: <Factory size={20} />,
    question: "What is Ready Mix Concrete?",
    answer:
      "Ready Mix Concrete (RMC) is concrete manufactured in a controlled batching plant using precise proportions of cement, aggregates, water, and admixtures. It is delivered to construction sites using transit mixers, ensuring consistent quality, strength, and workability.",
  },
  {
    icon: <Layers3 size={20} />,
    question: "Which concrete grade should I use?",
    answer:
      "The required concrete grade depends on the structural and load-bearing requirements of your project. Residential structures commonly use M20 or M25 grades, while commercial and infrastructure projects may require higher grades such as M30 or above.",
  },
  {
    icon: <ShieldCheck size={20} />,
    question: "How is concrete quality maintained?",
    answer:
      "Quality is maintained through automated batching systems, strict raw material testing, continuous batch monitoring, slump testing, cube testing, and adherence to Indian Standards and quality control protocols.",
  },
  {
    icon: <Factory size={20} />,
    question: "Do you provide customized concrete?",
    answer:
      "Yes. SSC READYMIX develops customized mix designs based on project requirements including strength, durability, workability, exposure conditions, pumpability, and structural performance.",
  },
  {
    icon: <Truck size={20} />,
    question: "What areas do you serve?",
    answer:
      "SSC READYMIX serves residential, commercial, industrial, and infrastructure projects across multiple regions with reliable dispatch planning, GPS-enabled transit mixers, and timely delivery support.",
  },
];

export default function Faq() {
  return (
    <main className="bg-[#fafafa]">
      <PageHero
        title="FAQ"
        subtitle="Frequently asked questions about SSC READYMIX concrete solutions and services."
        num="09"
      />

      <section className="py-20 md:py-28 relative overflow-hidden">
        {/* Background Glow */}
        <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-[#ef2435]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-[#ef2435]/5 rounded-full blur-3xl" />

        <div className="max-w-5xl mx-auto px-5 md:px-8 relative">
          {/* Heading */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full border border-[#ececef] bg-white mb-6">
              <HelpCircle size={15} className="text-[#ef2435]" />

              <span className="text-[11px] tracking-[0.3em] uppercase font-semibold text-[#ef2435]">
                Frequently Asked Questions
              </span>
            </div>

            <h2 className="text-4xl md:text-6xl font-black text-gray-900 leading-tight mb-6">
              Everything You Need
              <br />
              <span className="text-[#ef2435]">To Know</span>
            </h2>

            <p className="max-w-2xl mx-auto text-gray-600 leading-relaxed text-lg">
              Answers to common questions about ready mix concrete,
              quality standards, delivery, and customized solutions.
            </p>
          </div>

          {/* FAQ List */}
          <div className="space-y-5">
            {faqs.map((faq, index) => (
              <details
                key={index}
                className="group bg-white border border-[#ececef] rounded-[28px] overflow-hidden shadow-sm hover:shadow-xl hover:border-[#ef2435]/30 transition-all duration-500"
              >
                <summary className="list-none cursor-pointer p-7 md:p-8 flex items-center justify-between gap-5">
                  <div className="flex items-start gap-5">
                    {/* Icon */}
                    <div className="w-12 h-12 rounded-2xl bg-[#ef2435]/10 text-[#ef2435] flex items-center justify-center shrink-0">
                      {faq.icon}
                    </div>

                    {/* Question */}
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.25em] text-[#ef2435] font-semibold mb-2">
                        FAQ {String(index + 1).padStart(2, "0")}
                      </div>

                      <h3 className="text-lg md:text-2xl font-bold text-gray-900 leading-snug">
                        {faq.question}
                      </h3>
                    </div>
                  </div>

                  {/* Arrow */}
                  <div className="w-11 h-11 rounded-2xl border border-[#ececef] flex items-center justify-center group-open:bg-[#ef2435] group-open:border-[#ef2435] transition-all duration-300 shrink-0">
                    <ChevronDown className="text-gray-700 group-open:text-white group-open:rotate-180 transition-all duration-300" />
                  </div>
                </summary>

                {/* Content */}
                <div className="px-7 md:px-8 pb-8 md:pb-9">
                  <div className="pl-[68px]">
                    <div className="h-px bg-[#ececef] mb-6" />

                    <p className="text-gray-600 leading-[2] text-[16px]">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              </details>
            ))}
          </div>

          {/* Bottom CTA */}
          <div className="mt-20 rounded-[36px] bg-[#111] overflow-hidden relative p-10 md:p-14 text-center">
            <div className="absolute top-0 right-0 w-72 h-72 bg-[#ef2435]/10 rounded-full blur-3xl" />

            <div className="relative">
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/10 text-white/80 text-[11px] uppercase tracking-[0.3em] mb-6">
                Need More Information?
              </span>

              <h3 className="text-3xl md:text-5xl font-black text-white leading-tight mb-6">
                Let’s Build With
                <span className="text-[#ef2435]"> Confidence.</span>
              </h3>

              <p className="text-gray-300 max-w-2xl mx-auto leading-relaxed text-lg">
                Contact SSC READYMIX for concrete solutions,
                technical assistance, project support, and customized
                mix recommendations.
              </p>

              <div className="flex flex-wrap justify-center gap-4 mt-10">
                <a
                  href="/contact"
                  className="bg-[#ef2435] hover:bg-[#d81f2f] text-white px-8 py-4 rounded-full font-semibold transition-all duration-300"
                >
                  Contact Us
                </a>

                <a
                  href="/products"
                  className="bg-white/10 hover:bg-white/20 border border-white/10 text-white px-8 py-4 rounded-full font-semibold transition-all duration-300"
                >
                  Explore Products
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}